import { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';

import { ALL_OPTION_KEY, RAW_DATE_FORMAT } from '@/common/constants';
import { API_URL } from '@/dtos';

import { useGetEmployerIdParam } from '../../hooks';
import { useBaseReportTableQuery } from '../../queries';
import { CIPStatusReportTableParams } from '../CIPStatusReport.types';

export const QUERY_KEY = 'CIP_STATUS_REPORT_QUERY';

const MIN_SEARCH_LEN = 3;
const useCIPStatusReportQuery = (params: Partial<CIPStatusReportTableParams>) => {
  const currentSearch = useMemo(() => {
    if (!!params?.searchString && params?.searchString?.length >= MIN_SEARCH_LEN) {
      return params.searchString;
    }
    return null;
  }, [params.searchString]);

  const formattedStartDate = useMemo(() => {
    if (!params.startDate) {
      return null;
    }
    const parsedDate = dayjs(params.startDate);
    if (!parsedDate.isValid()) {
      return null;
    }
    return parsedDate.format(RAW_DATE_FORMAT);
  }, [params.startDate]);

  const formattedEndDate = useMemo(() => {
    if (!params.endDate) {
      return null;
    }
    const parsedDate = dayjs(params.endDate);
    if (!parsedDate.isValid()) {
      return null;
    }
    return parsedDate.format(RAW_DATE_FORMAT);
  }, [params.endDate]);
  const getEmployerIdParam = useGetEmployerIdParam();
  const employerId = getEmployerIdParam(params.employerId, params.employerIds);
  const employerIdsString = useMemo(() => params?.employerIds?.join(','), [params?.employerIds]);
  const base = useBaseReportTableQuery(
    API_URL.REPORTS_CIP_STATUS_REPORT_BREAKDOWN,
    [
      QUERY_KEY,
      currentSearch,
      params.employerId,
      employerIdsString,
      params.enrollmentStatuses?.join(','),
      params.employmentStatuses?.join(','),
      params.CIPStatuses?.join(','),
      params.reason?.join(','),
      params.accountStatuses?.join(','),
      params.startDate,
      params.endDate,
    ],
    {
      ...employerId,
      show_ssn: true,
      cip_status_start_date: formattedStartDate,
      cip_status_end_date: formattedEndDate,
      cip_statuses:
        params?.CIPStatuses && !params?.CIPStatuses.includes(ALL_OPTION_KEY)
          ? params?.CIPStatuses.join(',')
          : undefined,
      reason_types:
        params?.reason && !params?.reason.includes(ALL_OPTION_KEY) ? params?.reason.join(',') : undefined,
      employment_statuses:
        params?.employmentStatuses && !params?.employmentStatuses.includes(ALL_OPTION_KEY)
          ? params?.employmentStatuses.join(',')
          : undefined,
      enrollment_statuses:
        params?.enrollmentStatuses && !params?.enrollmentStatuses.includes(ALL_OPTION_KEY)
          ? params?.enrollmentStatuses.join(',')
          : undefined,
      account_statuses:
        params?.accountStatuses && !params?.accountStatuses.includes(ALL_OPTION_KEY)
          ? params?.accountStatuses.join(',')
          : undefined,

      isSSNShowed: params.isSSNShowed,
      search_value: currentSearch,
    },
    {
      isMultipleEmployers: params.isMultipleEmployer,
      isPreviewMode: params.isPreviewMode,
    }
  );

  useEffect(() => {
    base.onChangePlanOptions([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.employerId, employerIdsString]);

  useEffect(() => {
    base.setPage(0);
    base.setRawContentList([]);
    base.setRawHeaders([]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    params.employerId,
    params?.searchString,
    employerIdsString,
    params?.startDate,
    params?.endDate,
    params?.accountStatuses,
    params.employmentStatuses,
    params?.CIPStatuses,
    params?.enrollmentStatuses,
  ]);
  return base;
};

export default useCIPStatusReportQuery;
